import React, { useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Wallet, CheckCircle2, TrendingUp, TrendingDown, Banknote, CreditCard,
  AlertTriangle, Sparkles, X, Loader2, Trash2,
} from 'lucide-react';

/**
 * ArqueoDetalleModal — full breakdown of a saved cash-register closing.
 * Extracted from ArqueoPanel.js to keep that file focused on the form.
 * Adds keyboard ESC support, click-on-backdrop close, and a delete action.
 */
const ArqueoDetalleModal = ({ arqueo, onClose, onDelete, deleting }) => {
  const a = arqueo;

  // ESC closes — quality of life for keyboard users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const diffColor = Math.abs(a.diferencia) < 0.5
    ? 'text-emerald-600'
    : a.diferencia > 0 ? 'text-amber-600' : 'text-red-600';
  const diffBg = Math.abs(a.diferencia) < 0.5
    ? 'bg-emerald-50 border-emerald-200'
    : a.diferencia > 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  const fmt = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      data-testid="arqueo-detail-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Wallet size={20} />
              <h2 className="text-lg font-bold tracking-tight">Detalle de Arqueo</h2>
            </div>
            <p className="text-sm text-amber-50">
              {a.fecha} · <span className="capitalize">{a.turno}</span> · {a.creado_por || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            data-testid="arqueo-detail-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Reconciliation strip — cash */}
          <div className={`rounded-xl border-2 ${diffBg} p-4`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Diferencia efectivo (contado vs esperado)</p>
              {Math.abs(a.diferencia) < 0.5 && <CheckCircle2 className="text-emerald-500" size={20} />}
              {a.diferencia > 0.5 && <TrendingUp className="text-amber-500" size={20} />}
              {a.diferencia < -0.5 && <TrendingDown className="text-red-500" size={20} />}
            </div>
            <p className={`text-3xl font-extrabold ${diffColor}`}>
              {a.diferencia > 0 ? '+' : ''}{fmt(a.diferencia)}€
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {Math.abs(a.diferencia) < 0.5 ? 'Cuadre perfecto.' : a.diferencia > 0 ? 'Sobrante de caja.' : 'Faltante de caja.'}
            </p>
          </div>

          {/* Reconciliation strip — card */}
          {a.diferencia_tarjeta != null && (
            <div
              className={`rounded-xl border-2 p-4 ${Math.abs(a.diferencia_tarjeta) < 0.5 ? 'bg-emerald-50 border-emerald-200' : a.diferencia_tarjeta > 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}
              data-testid="arqueo-detail-diferencia-tarjeta"
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
                  <CreditCard size={12} /> Diferencia tarjeta (datáfono vs sistema)
                </p>
              </div>
              <p className={`text-3xl font-extrabold ${Math.abs(a.diferencia_tarjeta) < 0.5 ? 'text-emerald-600' : a.diferencia_tarjeta > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                {a.diferencia_tarjeta > 0 ? '+' : ''}{fmt(a.diferencia_tarjeta)}€
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                Datáfono: <span className="font-mono">{fmt(a.tarjeta_contado)}€</span> · Sistema: <span className="font-mono">{fmt(a.tarjeta_sistema)}€</span>
              </p>
            </div>
          )}

          {/* System totals */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Totales del sistema</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                  <Banknote size={14} /> Efectivo (sistema)
                </div>
                <p className="text-xl font-bold text-gray-800 font-mono">{fmt(a.efectivo_sistema)}€</p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                  <CreditCard size={14} /> Tarjeta (sistema)
                </div>
                <p className="text-xl font-bold text-gray-800 font-mono">{fmt(a.tarjeta_sistema)}€</p>
              </div>
              {Number(a.bizum_sistema) > 0 && (
                <div className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    Bizum (sistema)
                  </div>
                  <p className="text-xl font-bold text-gray-800 font-mono">{fmt(a.bizum_sistema)}€</p>
                </div>
              )}
              <div className="rounded-lg border border-gray-100 p-3 bg-white col-span-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 uppercase font-semibold">Total sistema</span>
                  <span className="text-2xl font-extrabold text-gray-900 font-mono">{fmt(a.total_sistema)}€</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">{a.num_ventas ?? 0} ventas registradas</p>
              </div>
            </div>
          </div>

          {/* Cash reconciliation breakdown */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Cuadre de efectivo</p>
            <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 text-sm">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-gray-600">Fondo inicial</span>
                <span className="font-mono font-medium">{fmt(a.fondo_inicial)}€</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-gray-600">+ Efectivo de ventas</span>
                <span className="font-mono font-medium">{fmt(a.efectivo_sistema)}€</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-gray-600">− Gastos en efectivo</span>
                <span className="font-mono font-medium">−{fmt(a.gastos_efectivo)}€</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                <span className="text-gray-700 font-medium">= Esperado en caja</span>
                <span className="font-mono font-bold">{fmt(a.efectivo_esperado)}€</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-gray-700 font-medium">Efectivo contado</span>
                <span className="font-mono font-bold">{fmt(a.efectivo_contado)}€</span>
              </div>
              {Number(a.propinas) > 0 && (
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-gray-500">Propinas (no afecta cuadre)</span>
                  <span className="font-mono text-gray-500">{fmt(a.propinas)}€</span>
                </div>
              )}
            </div>
          </div>

          {/* Next-day float */}
          {Number(a.fianza_dia_siguiente) > 0 && (
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-3" data-testid="arqueo-detail-fianza">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1.5">
                  <Wallet size={12} /> Fianza para mañana
                </span>
                <span className="font-mono font-bold text-lg text-emerald-700">{fmt(a.fianza_dia_siguiente)}€</span>
              </div>
              {a.a_retirar != null && (
                <div className="flex items-center justify-between mt-1 text-xs text-gray-600">
                  <span>Retirado de caja al cierre</span>
                  <span className="font-mono font-semibold">{fmt(a.a_retirar)}€</span>
                </div>
              )}
            </div>
          )}

          {/* Insights */}
          {a.insights?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="text-amber-500" size={16} />
                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Insights de la IA</p>
              </div>
              <div className="space-y-2">
                {a.insights.map((ins, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700 p-2 bg-amber-50/40 rounded-lg" data-testid={`arqueo-detail-insight-${i}`}>
                    {ins.tipo === 'ok' && <CheckCircle2 className="text-emerald-500 mt-0.5 flex-shrink-0" size={16} />}
                    {ins.tipo === 'sobrante' && <TrendingUp className="text-amber-500 mt-0.5 flex-shrink-0" size={16} />}
                    {ins.tipo === 'faltante' && <TrendingDown className="text-red-500 mt-0.5 flex-shrink-0" size={16} />}
                    {ins.tipo === 'alerta' && <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />}
                    {ins.tipo === 'info' && <Sparkles className="text-amber-500 mt-0.5 flex-shrink-0" size={16} />}
                    <span>{ins.msg}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {a.notas && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Notas</p>
              <p className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">{a.notas}</p>
            </div>
          )}

          {/* Meta */}
          <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-100">
            ID: <span className="font-mono">{a.id}</span> · Creado: {a.created_at}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-5 py-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(a.id)}
            disabled={deleting}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="arqueo-detail-delete-btn"
          >
            {deleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
            Eliminar
          </Button>
          <Button onClick={onClose} className="bg-gray-900 hover:bg-gray-800" data-testid="arqueo-detail-done-btn">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArqueoDetalleModal;
